import { FormatWidth, getLocaleTimeFormat } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, Input, LOCALE_ID, Optional, Self, SimpleChanges } from '@angular/core';
import { NgControl } from '@angular/forms';
import { NgbTimeStruct } from '@ng-bootstrap/ng-bootstrap';
import momentImported from 'moment';
import { FormControlBase } from '../../common/form-control-base/form-control-base';
import { TCM_FORM_CONFIG, TCMFormConfig } from '../../tcm-form-config';
import { TimeLocaleHelper } from '../../helpers/time-locale-helper';

import { maskitoTimeOptionsGenerator } from '@maskito/kit';

const moment = momentImported;

@Component({
  selector: 'waf-time-input',
  templateUrl: './time-input.component.html',
  styleUrls: ['./time-input.component.scss'],
  inputs: ['label', 'disabled', 'hideValidationMessages', 'isReadonlyValue', 'placeholder', 'tabindex'],
  standalone: false
})
export class TimeInputComponent extends FormControlBase<string> {
  @Input() meridian = false;
  @Input() minuteStep = 1;

  displayValue: string | null = null;
  timeModel: NgbTimeStruct | null = null;

  // Maskito options
  maskOptions: any;

  private format: string;
  private customTimepickerMomentLocaleName: string;

  constructor(
    @Self() @Optional() protected override ngModel: NgControl,
    @Inject(LOCALE_ID) private locale: string,
    @Optional() @Inject(TCM_FORM_CONFIG) protected config: TCMFormConfig,
    private ref: ChangeDetectorRef
  ) {
    super('waf-time-input', ngModel);

    if (this.config?.timePicker) {
      this.minuteStep = this.config.timePicker.minuteStep || 1;
    }
  }

  override onInit() {
    if (!this.format) {
      const localeTimeFormat = moment.localeData(this.locale).longDateFormat('LT');
      this.meridian = localeTimeFormat.includes('A');
      this.format = this.meridian ? 'hh:mm A' : 'HH:mm';
    }

    this.setCustomMomentLocale();
    this.setupMask();
  }

  override ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    if (changes.meridian) {
      this.format = this.meridian ? 'hh:mm A' : 'HH:mm';
      this.setupMask();

      if (this.value) {
        const momentObj = this.getDisplayValueMoment(this.value);
        this.displayValue = this.convertMomentToDisplayValue(momentObj);
      }
    }
  }

  onFocusOut() {
    if (!this.value) {
      this.displayValue = null;
    } else {
      const momentObj = this.getDisplayValueMoment(this.value);
      this.displayValue = momentObj.isValid()
        ? this.convertMomentToDisplayValue(momentObj)
        : this.value;
    }
    this.ref.detectChanges();
    this.touch();
  }

  onDisplayValueChange(displayValue: string) {
    if (!displayValue) {
      this.value = null;
      this.timeModel = null;
    } else {
      const momentObj = this.momentWithLocale(displayValue, this.format);
      if (momentObj.isValid()) {
        this.value = this.convertMomentToIsoValue(momentObj);
        this.timeModel = {
          hour: momentObj.hour(),
          minute: momentObj.minute(),
          second: 0
        };
      } else {
        this.value = null;
        this.timeModel = null;
      }
    }
  }

  onTimeModelChange(timePickerValue: NgbTimeStruct) {
    this.timeModel = timePickerValue;
    if (!timePickerValue) {
      this.value = null;
      this.displayValue = null;
    } else {
      const momentObj = this.momentWithLocale().set({
        hour: timePickerValue.hour,
        minute: timePickerValue.minute
      });
      this.value = this.convertMomentToIsoValue(momentObj);
      this.displayValue = this.convertMomentToDisplayValue(momentObj);
    }
  }

  protected override onValueWritten(previousValue: string, newValue: string) {
    if (newValue === previousValue) return;
    if (!newValue) {
      this.displayValue = null;
      this.timeModel = null;
    } else {
      const momentObj = this.momentWithLocale(newValue, this.format);
      this.displayValue = this.convertMomentToDisplayValue(momentObj);
      this.timeModel = {
        hour: momentObj.hour(),
        minute: momentObj.minute(),
        second: 0
      };
    }
  }

  private getDisplayValueMoment(displayValue: string): momentImported.Moment {
    return this.momentWithLocale(displayValue, this.format);
  }

  private convertMomentToIsoValue(obj: momentImported.Moment): string {
    return obj.format('HH:mm');
  }

  private convertMomentToDisplayValue(obj: momentImported.Moment): string {
    return obj.format(this.format);
  }

  private setupMask() {
    this.maskOptions = maskitoTimeOptionsGenerator({
      mode: this.meridian ? 'HH:MM AA' : 'HH:MM'
    });
  }

  private setCustomMomentLocale() {
    this.customTimepickerMomentLocaleName = TimeLocaleHelper.setCustomMomentLocale(this.locale);
  }

  private momentWithLocale(...args: any[]): momentImported.Moment {
    if (args.length) {
      return moment(...args).locale(this.customTimepickerMomentLocaleName);
    }
    return moment().locale(this.customTimepickerMomentLocaleName);
  }
}
